import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';

// services
import { searchLocation } from '../../services/locations/searchLocation';

// redux
import { addSupportedLocation } from '../../stores/locations';

/**
 * Modal for adding a new supported location.
 *
 * Flow:
 * 1. User types a place name → clicks search
 * 2. AWS Places API returns results → displayed as selectable cards
 * 3. User picks one → sees the full preview (state, municipality, etc.)
 * 4. User clicks "Proceed" → POST to backend → success/error
 */
export const ModalAddLocation = ({ isOpen, onClose }) => {

  const dispatch = useDispatch();
  const { creating } = useSelector((state) => state.locations);

  // Local state
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  // ===== SEARCH =====
    const handleSearch = async () => {
    if (!query.trim()) return;
    if (query.trim().length > 100) {
      setError('Search query is too long. Please use fewer than 100 characters.');
      return;
    }
    setError('');
    setSearching(true);
    setResults([]);
    setSelected(null);

    try {
      const places = await searchLocation(query.trim());
      if (places.length === 0) {
        setError('No results found. Try a more specific search.');
      }
      setResults(places);
    } catch (err) {
      console.error('ModalAddLocation@handleSearch', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // ===== SUBMIT =====
  const handleSubmit = async () => {
    if (!selected) return;

    // Validate required fields
   //if (!selected.postal_code || selected.postal_code.length < 5) {
      //  toast.error('Postal code is required (minimum 5 digits). Please enter it manually.');
       // return;
   // }

    try {
      await dispatch(addSupportedLocation(selected)).unwrap();
      toast.success(`Added ${selected.municipality}, ${selected.state} successfully!`);
      handleReset();
      onClose();
    } catch (err) {
      console.error('ModalAddLocation@handleSubmit', err);
      toast.error('Failed to add location. Please try again.');
    }
  };

  // ===== RESET =====
  const handleReset = () => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setError('');
  };

  // ===== CLOSE =====
  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Add New Supported Location</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">

          {/* Search bar */}
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="e.g. Alor Setar, Kedah"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !query.trim()}
              aria-label="Search"
              className="inline-flex items-center px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Loading */}
          {searching && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
              <p className="ml-2 text-sm text-gray-500">Searching...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {/* Results list (pick one) */}
          {results.length > 0 && !selected && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase">Select a result:</p>
              {results.map((place, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelected(place)}
                  className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition"
                >
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{place.label}</p>
                      <p className="text-xs text-gray-500">
                        {place.municipality}, {place.state} — {place.postal_code}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Preview selected */}
          {selected && (
            <div className="rounded-md border border-primary-200 bg-primary-50 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-4 w-4 text-primary-600" />
                <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">Confirm details</p>
              </div>
              <dl className="divide-y divide-primary-100 text-sm">
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500">State</dt>
                  <dd className="font-medium text-slate-800">{selected.state}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500">Municipality:</dt>
                  <dd className="font-medium text-slate-800">{selected.municipality}</dd>
                </div>
                <div>
                  { /*<span className="text-gray-500">Post Code:</span>
                    <input
                      type="text"
                      value={selected.postal_code || ''}
                      onChange={(e) => setSelected({ ...selected, postal_code: e.target.value })}
                      placeholder="Enter postal code (e.g. 78000)"
                      maxLength={5}
                      className="mt-1 block w-full rounded-md border-red-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    /> */}
                </div>
               <div className="flex justify-between py-2">
                  <dt className="text-gray-500">Country</dt>
                  <dd className="font-medium text-slate-800">{selected.country === 'MYS' ? 'Malaysia' : selected.country}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500">Country Code</dt>
                  <dd className="font-medium text-slate-800">{selected.country_code}</dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500">Label</dt>
                  <dd className="font-medium text-slate-800">{selected.label}</dd>
                </div>
              </dl>

              <button
                onClick={() => setSelected(null)}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                ← Pick a different result
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selected || creating}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {creating ? 'Saving...' : 'Proceed — Add Location'}
          </button>
        </div>
      </div>
    </div>
  );
};